import {CBARCameraResolution} from "../../CBARContext";
import {canvasCreate} from "../Utils";

export const isPortrait = window.outerWidth < window.outerHeight;

const createVideoElement = () => {
    return document.createElement('video');
}

const getWidth = (r:number, h:number) => {
    return r * h;
}

const getHeight = (r:number, w:number) => {
    return w / r;
}

//w3 specs for camera https://w3c.github.io/mediacapture-main/getusermedia.html#dom-mediatrackcapabilities
//info on camera settings https://oberhofer.co/mediastreamtrack-and-its-capabilities/

function getMaxAvailableSize(r:number, maxWidth:number, maxHeight:number) {
    if (maxWidth) {
        const _height = getHeight(r, maxWidth);

        if (_height <= maxHeight) {
            return {
                width: maxWidth,
                height: _height,
            };
        }
    }

    return {
        width: getWidth(r, maxHeight),
        height: maxHeight,
    };
}

function getMinAvailableSize(r:number, minWidth:number, minHeight:number) {
    if (minWidth) {
        const _height = getHeight(r, minWidth);

        if (_height > minHeight) {
            return {
                width: minWidth,
                height: _height,
            };
        }
    }

    return {
        width: getWidth(r, minHeight),
        height: minHeight,
    };
}

export type CBARVideoSettings = {
    deviceId:string
    sourceWidth:number
    sourceHeight:number
    displayWidth:number,
    displayHeight:number
}

export default class CBARVideoCapture {

    public readonly video:HTMLVideoElement
    private track:MediaStreamTrack|null

    constructor() {
        this.video = createVideoElement();
        this.video.muted = true;
        this.video.playsInline = true;

        this.track = null;
    }

    public captureWidth = 0;
    public captureHeight = 0;

    setSourceSize(width:number, height:number) {
        console.log(`Source size ${width}x${height}`)

        this.captureWidth = width;
        this.captureHeight = height;
    }

    started = false

    start(resolution:CBARCameraResolution, facing:string|undefined) {

        //console.log("Using recommended res", resolution);

        this.started = true;
        const cfg:MediaStreamConstraints = {
            video: {
                width: { ideal: resolution > 0 ? resolution : CBARCameraResolution.Default },
                facingMode:facing,
            },
        };

        //const ua = navigator.userAgent;

        // if (/android/i.test(ua) && isPortrait) {
        //     // @ts-ignore
        //     cfg.video.aspectRatio.exact = 1 / cfg.video.aspectRatio.exact;
        // }

        return navigator.mediaDevices.getUserMedia(cfg).then((stream) => {
            if (stream) {
                const tracks = stream.getTracks();

                if (!this.started) {
                    tracks.forEach(t => t.stop());
                    return null;
                }
                if ('srcObject' in this.video) {
                    this.video.srcObject = stream;
                }

                this.track = tracks[0];
                //console.log("Track info", this.track.getSettings())

                //alert(`Track info ${JSON.stringify(this.track.getSettings())}`);

                return this.video.play()
                    .then(() => this.setSourceSize(this.video.videoWidth, this.video.videoHeight));
            }
            throw new Error('getUserMedia not found or no stream was created');
        });
    }

    public stop() {
        this.started = false;
        if (this.track) {
            this.track.stop();
            this.track = null;
        }
    }

    public processFrame(displayCanvas:HTMLCanvasElement) {

        const displayContext = displayCanvas.getContext("2d");
        const [ow, oh] = [displayCanvas.width, displayCanvas.height];
        const sar = this.video.videoWidth/this.video.videoHeight;
        const oar = ow/oh;

        //sar:1.7777777777777777 (1280, 720) oar:0.5622188905547226 (375, 667)
        const [w, h] = sar > oar ? [sar * oh, oh] : [ow, oh / sar];

        displayContext?.drawImage(this.video, (ow - w) / 2,(oh - h) / 2, w, h);
    }

    public get resolution() : [number, number] | undefined {
        if (this.track) {
            const width = this.track.getSettings().width;
            const height = this.track.getSettings().height;
            if (width && height) {
                return [width, height];
            }
        }
        return undefined;
    }
}